import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { uploadToPresignedUrl } from "@/api/client";
import { useLogoUploadTarget, useUpdateProject } from "@/api/hooks";
import type { Project } from "@/api/types/Project";
import { ConfirmDialog } from "@/components/Dialog";
import { ProjectLogo } from "@/components/ProjectLogo";
import { useToast } from "@/components/Toast";
import { Button, Field, Input, Notice, Textarea } from "@/components/ui";
import { AppsSection } from "./AppsSection";
import { ChannelsSection } from "./ChannelsSection";
import { GitlabSection } from "./GitlabSection";
import { FieldsSection, ScrumSection, SprintSettingsSection, StatusesSection } from "./WorkSettings";
import { ChecklistTemplatesSection, RecurringSection, TaskTemplatesSection } from "./TemplatesSettings";
import { useParams } from "react-router";
import { SETTINGS_SECTIONS, SettingsTabs } from "@/components/AreaTabs";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { useProjectContext } from "./ProjectLayout";

const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];
const LOGO_MAX = 2 * 1024 * 1024;

export function ProjectSettings() {
  const { t } = useTranslation();
  const project = useProjectContext();

  const param = useParams().section;

  if (project.my_role !== "manager") return <Notice>{t("settings.forbidden")}</Notice>;
  const section = param === undefined ? "general" : SETTINGS_SECTIONS.find((s) => s === param);
  if (!section) return <NotFoundPage />;

  return (
    <div className="flex flex-col gap-5">
      <SettingsTabs project={project} />
      <div className="flex max-w-3xl flex-col gap-5">
        {section === "general" && (
          <>
            {/* key: reset the form when the project data changes underneath it */}
            <GeneralForm key={`${project.id}-${project.name}-${project.description}`} project={project} />
            <LogoSection project={project} />
            <ArchiveSection project={project} />
          </>
        )}
        {section === "workflow" && (
          <>
            <StatusesSection project={project} />
            <FieldsSection project={project} />
            <ScrumSection project={project} />
            <SprintSettingsSection project={project} />
          </>
        )}
        {section === "templates" && (
          <>
            <TaskTemplatesSection project={project} />
            <ChecklistTemplatesSection project={project} />
            <RecurringSection project={project} />
          </>
        )}
        {section === "bugs" && (
          <ComponentsSection key={project.components.join("\u0000")} project={project} />
        )}
        {section === "apps" && <AppsSection project={project} />}
        {section === "channels" && <ChannelsSection project={project} />}
        {section === "gitlab" && <GitlabSection project={project} />}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="card flex flex-col gap-4 p-5">
      <h3 className="m-0 label-caps">{title}</h3>
      {children}
    </section>
  );
}

function GeneralForm({ project }: { project: Project }) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateProject(project.key);
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description);
  const dirty = name.trim() !== project.name || description.trim() !== project.description;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate(
      { name: name.trim(), description: description.trim() },
      { onSuccess: () => toast.success(t("settings.saved")), onError: (err) => toast.error(err) },
    );
  };

  return (
    <Section title={t("settings.general")}>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label={t("projects.key")}>
          {(p) => <Input {...p} value={project.key} disabled readOnly className="max-w-[160px] font-mono" />}
        </Field>
        <Field label={t("projects.name")}>
          {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} />}
        </Field>
        <Field label={t("projects.description")} optional>
          {(p) => <Textarea {...p} value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />}
        </Field>
        <div>
          <Button type="submit" variant="primary" loading={update.isPending} disabled={!dirty || !name.trim()}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Section>
  );
}

function ComponentsSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateProject(project.key);
  const [items, setItems] = useState<string[]>(project.components);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState<number | null>(null);
  const dirty = JSON.stringify(items) !== JSON.stringify(project.components);
  const k = (key: string) => `components.${key}`;
  const has = (name: string, except?: number) =>
    items.some((c, i) => i !== except && c.toLowerCase() === name.toLowerCase());

  const add = (e: FormEvent) => {
    e.preventDefault();
    const name = draft.trim();
    if (!name || has(name)) return;
    setItems((cur) => [...cur, name]);
    setDraft("");
  };

  const rename = (i: number, value: string) => {
    const name = value.trim();
    setEditing(null);
    if (!name || has(name, i)) return;
    setItems((cur) => cur.map((c, j) => (j === i ? name : c)));
  };

  const save = () =>
    update.mutate(
      { components: items },
      { onSuccess: () => toast.success(t(k("saved"))), onError: (err) => toast.error(err) },
    );

  return (
    <Section title={t(k("title"))}>
      <p className="m-0 text-sm text-muted">{t(k("intro"))}</p>
      {items.length === 0 ? (
        <p className="m-0 text-sm text-muted">{t(k("empty"))}</p>
      ) : (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
          {items.map((c, i) => (
            <li key={`${c}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-surface-2 py-0.5 pr-1 pl-3 text-sm">
              {editing === i ? (
                <input
                  aria-label={t(k("rename"), { name: c })}
                  defaultValue={c}
                  autoFocus
                  maxLength={80}
                  onBlur={(e) => rename(i, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") rename(i, (e.target as HTMLInputElement).value);
                    if (e.key === "Escape") setEditing(null);
                  }}
                  className="w-32 border-0 bg-transparent py-0.5 outline-none"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setEditing(i)}
                  aria-label={t(k("rename"), { name: c })}
                  className="py-0.5 hover:underline"
                >
                  {c}
                </button>
              )}
              <Button
                size="sm"
                variant="ghost"
                icon="x"
                aria-label={t(k("remove"), { name: c })}
                onClick={() => setItems((cur) => cur.filter((_, j) => j !== i))}
                className="h-6 px-1"
              />
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="flex flex-wrap items-center gap-2">
        <Input
          aria-label={t(k("add"))}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t(k("placeholder"))}
          maxLength={80}
          className="max-w-[240px]"
        />
        <Button type="submit" icon="plus" disabled={!draft.trim() || has(draft.trim())}>
          {t(k("add"))}
        </Button>
      </form>
      <div>
        <Button variant="primary" loading={update.isPending} disabled={!dirty} onClick={save}>
          {t("common.save")}
        </Button>
      </div>
    </Section>
  );
}

function LogoSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const toast = useToast();
  const target = useLogoUploadTarget(project.key);
  const update = useUpdateProject(project.key);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) return toast.error(t("settings.logoBadType"));
    if (file.size > LOGO_MAX) return toast.error(t("settings.logoTooBig"));

    setUploading(true);
    try {
      const { key, upload_url } = await target.mutateAsync({ content_type: file.type, size: file.size });
      await uploadToPresignedUrl(upload_url, file);
      await update.mutateAsync({ logo_key: key });
      toast.success(t("settings.logoUploaded"));
    } catch (err) {
      toast.error(err);
    } finally {
      setUploading(false);
    }
  };

  const remove = () =>
    update.mutate(
      { remove_logo: true },
      { onSuccess: () => toast.success(t("settings.logoRemoved")), onError: (err) => toast.error(err) },
    );

  return (
    <Section title={t("settings.logo")}>
      <div className="flex flex-wrap items-center gap-4">
        <ProjectLogo project={project} size={72} />
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            <Button icon="image" loading={uploading} onClick={() => fileInput.current?.click()}>
              {uploading ? t("settings.uploading") : project.logo_url ? t("settings.replaceLogo") : t("settings.uploadLogo")}
            </Button>
            {project.logo_url && (
              <Button variant="ghost" onClick={remove} disabled={uploading || update.isPending}>
                {t("settings.removeLogo")}
              </Button>
            )}
          </div>
          <span className="text-xs text-muted">{t("settings.logoHint")}</span>
        </div>
        <input
          ref={fileInput}
          type="file"
          accept={LOGO_TYPES.join(",")}
          onChange={(e) => void onFile(e)}
          className="hidden"
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>
    </Section>
  );
}

function ArchiveSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateProject(project.key);
  const [confirming, setConfirming] = useState(false);

  const setArchived = (archived: boolean) =>
    update.mutate(
      { archived },
      {
        onSuccess: () => {
          toast.success(archived ? t("settings.archivedToast") : t("settings.unarchivedToast"));
          setConfirming(false);
        },
        onError: (err) => toast.error(err),
      },
    );

  return (
    <Section title={t("settings.archiveSection")}>
      <p className="m-0 text-sm text-text-2">{t("settings.archiveBody")}</p>
      <div>
        {project.archived ? (
          <Button icon="archive" loading={update.isPending} onClick={() => setArchived(false)}>
            {t("settings.unarchive")}
          </Button>
        ) : (
          <Button variant="danger" icon="archive" onClick={() => setConfirming(true)}>
            {t("settings.archive")}
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => setArchived(true)}
        loading={update.isPending}
        danger
        title={t("settings.archiveTitle", { name: project.name })}
        body={t("settings.archiveBody")}
        confirmLabel={t("settings.archive")}
      />
    </Section>
  );
}
